<template>
  <div class="sensors-container">
    <!-- Header -->
    <div class="sensors-header">
      <div class="header-info">
        <span class="material-symbols-outlined header-icon">sensors</span>
        <div>
          <h3 class="header-title">Mesures</h3>
          <p class="header-subtitle">{{ sensors.length }} mesure{{ sensors.length !== 1 ? 's' : '' }} configurée{{ sensors.length !== 1 ? 's' : '' }}</p>
        </div>
      </div>
      <button
          v-if="editable"
          class="btn btn-primary"
          @click="openAddModal"
      >
        <span class="material-symbols-outlined">add</span>
        <span class="btn-text">Ajouter</span>
      </button>
    </div>

    <!-- Sensors Grid/Table -->
    <div class="sensors-content" v-if="sensors && sensors.length > 0">
      <!-- Desktop Table View -->
      <div class="sensors-table">
        <div class="table-header">
          <span class="col-name">Intitulé</span>
          <span class="col-csv">Colonne CSV</span>
          <span class="col-unit">Unité</span>
          <span class="col-min">Min</span>
          <span class="col-max">Max</span>
          <span class="col-formula">Formule</span>
          <span class="col-actions" v-if="editable"></span>
        </div>

        <div
            class="table-row"
            v-for="(sensor, index) in sensors"
            :key="index"
            :class="{ 'has-alert': sensor.min || sensor.max }"
        >
          <!-- Name -->
          <div class="col-name">
            <input
                v-if="editable"
                type="text"
                class="inline-input"
                v-model="sensor.name"
                placeholder="Nom..."
            />
            <span class="cell-value" v-else>{{ sensor.name || '—' }}</span>
          </div>

          <!-- CSV Column -->
          <div class="col-csv">
            <input
                v-if="editable"
                type="text"
                class="inline-input mono"
                v-model="sensor.csv_column"
                placeholder="colonne..."
            />
            <code class="cell-code" v-else>{{ sensor.csv_column || '—' }}</code>
          </div>

          <!-- Unit -->
          <div class="col-unit">
            <input
                v-if="editable"
                type="text"
                class="inline-input unit-input"
                v-model="sensor.unit_of_measure"
                placeholder="—"
            />
            <span class="cell-unit" v-else>{{ sensor.unit_of_measure || '—' }}</span>
          </div>

          <!-- Min -->
          <div class="col-min">
            <input
                v-if="editable"
                type="number"
                class="inline-input number-input"
                v-model="sensor.min"
                placeholder="—"
            />
            <span class="cell-alert" :class="{ active: sensor.min }" v-else>
              {{ sensor.min || '—' }}
            </span>
          </div>

          <!-- Max -->
          <div class="col-max">
            <input
                v-if="editable"
                type="number"
                class="inline-input number-input"
                v-model="sensor.max"
                placeholder="—"
            />
            <span class="cell-alert" :class="{ active: sensor.max }" v-else>
              {{ sensor.max || '—' }}
            </span>
          </div>

          <!-- Formula -->
          <div class="col-formula">
            <input
                v-if="editable"
                type="text"
                class="form-input mono"
                v-model="sensor.calcForm"
                placeholder="Ex: Mesure1 + Mesure2 / 2"
            />
            <code class="cell-formula" v-else>{{ sensor.calcForm || '—' }}</code>
          </div>

          <!-- Actions -->
          <div class="col-actions" v-if="editable">
            <button class="btn-delete" @click="deleteSensor(sensor)" title="Supprimer">
              <span class="material-symbols-outlined">delete</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Sensor Modal -->
    <transition name="modal-fade">
      <div class="modal-overlay" v-if="sensorModal" @click.self="cancel">
        <div class="modal-card">
          <div class="modal-header">
            <div class="modal-title">
              <span class="material-symbols-outlined">add_circle</span>
              <h3>Nouvelle mesure</h3>
            </div>
            <button class="modal-close" @click="cancel">
              <span class="material-symbols-outlined">close</span>
            </button>
          </div>

          <div class="modal-body" v-if="ready">
            <!-- Sensor Type Selector -->
            <div class="type-selector">
              <label class="field-label">Type de mesure (optionnel)</label>
              <div class="type-grid">
                <button
                    class="type-option"
                    :class="{ selected: !sensorCreated.type }"
                    @click="clearType"
                >
                  <span class="material-symbols-outlined">edit_note</span>
                  <span>Personnalisé</span>
                </button>
                <button
                    v-for="type in sensorTypes.slice(0, 5)"
                    :key="type.id"
                    class="type-option"
                    :class="{ selected: sensorCreated.type && sensorCreated.type.id === type.id }"
                    @click="changeType(type)"
                >
                  <span class="material-symbols-outlined">{{ getTypeIcon(type.name) }}</span>
                  <span>{{ type.name }}</span>
                </button>
              </div>
              <v-select
                  v-if="sensorTypes.length > 5"
                  :items="sensorTypes"
                  label="Ou sélectionner dans la liste..."
                  item-text="name"
                  item-value="id"
                  return-object
                  clearable
                  solo
                  flat
                  hide-details
                  dense
                  v-model="sensorCreated.type"
                  @change="changeType"
                  class="type-select"
              ></v-select>
            </div>

            <div class="form-divider"></div>

            <!-- Form Fields -->
            <div class="form-grid">
              <div class="form-field full">
                <label class="field-label required">Intitulé</label>
                <input
                    type="text"
                    class="form-input"
                    v-model="sensorCreated.name"
                    placeholder="Ex: Température, Humidité..."
                    ref="nameInput"
                />
              </div>

              <div class="form-field">
                <label class="field-label required">Colonne CSV</label>
                <input
                    type="text"
                    class="form-input mono"
                    v-model="sensorCreated.csv_column"
                    placeholder="Ex: temp_ext"
                />
              </div>

              <div class="form-field">
                <label class="field-label">Unité de mesure</label>
                <input
                    type="text"
                    class="form-input"
                    v-model="sensorCreated.unit_of_measure"
                    placeholder="Ex: °C, %, ppm..."
                />
              </div>

              <div class="form-field">
                <label class="field-label">
                  Valeur min (alerte)
                  <span class="field-hint">Optionnel</span>
                </label>
                <input
                    type="number"
                    class="form-input"
                    v-model="sensorCreated.min"
                    placeholder="—"
                />
              </div>

              <div class="form-field">
                <label class="field-label">
                  Valeur max (alerte)
                  <span class="field-hint">Optionnel</span>
                </label>
                <input
                    type="number"
                    class="form-input"
                    v-model="sensorCreated.max"
                    placeholder="—"
                />
              </div>

              <div class="form-field full">
                <label class="field-label">
                  Formule de calcul
                  <span class="field-hint">Optionnel</span>
                </label>
              </div>

              <div class="form-field full">
                <label class="field-label">
                  Mesure combinée (somme de plusieurs mesures)
                  <span class="field-hint">Optionnel</span>
                </label>
                <div class="combined-toggle">
                  <input
                      type="checkbox"
                      id="isCombined"
                      v-model="sensorCreated.isCombined"
                  />
                  <label for="isCombined">Cette mesure est une somme de plusieurs colonnes CSV</label>
                </div>
              </div>

              <div class="form-field full" v-if="sensorCreated.isCombined">
                <label class="field-label required">
                  Colonnes CSV à additionner
                  <span class="field-hint">Séparez par des virgules</span>
                </label>
                <input
                    type="text"
                    class="form-input mono"
                    v-model="sensorCreated.combinedColumns"
                    placeholder="Ex: connector1_channel0, connector2_channel0, connector3_channel0"
                />
                <p class="field-help">
                  Les valeurs de ces colonnes seront additionnées automatiquement dans Elasticsearch
                </p>
              </div>
              <SensorFormulaCalculator v-model="sensorCreated.calcForm"  style="grid-column: span 2;"/>
            </div>
          </div>

          <!-- Loading State -->
          <div class="modal-body loading" v-else>
            <div class="loader">
              <div class="loader-spinner"></div>
              <span>Chargement des types...</span>
            </div>
          </div>

          <div class="modal-footer">
            <button class="btn btn-ghost" @click="cancel">
              Annuler
            </button>
            <button
                class="btn btn-primary"
                @click="handleSavedSensorForm"
                :disabled="!canSave"
            >
              <span class="material-symbols-outlined">add</span>
              Ajouter la mesure
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Delete Confirmation Modal -->
    <transition name="modal-fade">
      <div class="modal-overlay" v-if="deleteModal" @click.self="cancelDelete">
        <div class="modal-card modal-sm">
          <div class="delete-content">
            <div class="delete-icon">
              <span class="material-symbols-outlined">warning</span>
            </div>
            <h3>Supprimer cette mesure ?</h3>
            <p class="delete-sensor-name">{{ sensorToDelete ? sensorToDelete.name : '' }}</p>
            <p class="delete-warning">Cette action est irréversible.</p>
          </div>
          <div class="modal-footer">
            <button class="btn btn-ghost" @click="cancelDelete">
              Annuler
            </button>
            <button class="btn btn-danger" @click="confirmDelete">
              <span class="material-symbols-outlined">delete</span>
              Supprimer
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>

import SensorFormulaCalculator from "@/components/SensorFormulaCalculator"

export default {
  name: 'SiteSensors',

  components: {
    SensorFormulaCalculator
  },

  props: {
    sensors: {
      type: Array,
      default: () => []
    },
    editable: {
      type: Boolean,
      default: true
    },
  },

  data() {
    return {
      ready: false,
      sensorTypes: [],
      sensorModal: false,
      deleteModal: false,
      sensorToDelete: null,
      sensorCreated: {
        type: null,
        name: '',
        csv_column: '',
        unit_of_measure: '',
        min: '',
        max: '',
        calcForm: '',
        isCombined: false,
        combinedColumns: ''
      },
    };
  },

  computed: {
    canSave() {
      return this.sensorCreated.name && this.sensorCreated.csv_column;
    }
  },

  mounted() {
    this.getSensorTypes().then((result) => {
      this.sensorTypes = result.data;
      this.ready = true;
    }).catch(() => {
      this.ready = true;
    });
  },

  methods: {
    getTypeIcon(name) {
      const icons = {
        'température': 'thermostat',
        'humidité': 'water_drop',
        'pression': 'speed',
        'co2': 'co2',
        'luminosité': 'light_mode',
        'vent': 'air',
        'pluie': 'rainy',
        'default': 'sensors'
      };
      const key = name.toLowerCase();
      for (const [k, v] of Object.entries(icons)) {
        if (key.includes(k)) return v;
      }
      return icons.default;
    },

    openAddModal() {
      this.resetForm();
      this.sensorModal = true;
      this.$nextTick(() => {
        if (this.$refs.nameInput) {
          this.$refs.nameInput.focus();
        }
      });
    },

    clearType() {
      this.sensorCreated.type = null;
    },

    changeType(sensorType) {
      if (!sensorType) {
        this.resetForm();
        return;
      }
      this.sensorCreated.type = sensorType;
      this.sensorCreated.name = sensorType.name || '';
      this.sensorCreated.csv_column = sensorType.csv_column || '';
      this.sensorCreated.unit_of_measure = sensorType.unit_of_measure || '';
      this.sensorCreated.min = sensorType.min || '';
      this.sensorCreated.max = sensorType.max || '';
      this.sensorCreated.calcForm = sensorType.calcForm || '';
      this.sensorCreated.isCombined = sensorType.isCombined || false;
      this.sensorCreated.combinedColumns = sensorType.combinedColumns || '';
    },

    resetForm() {
      this.sensorCreated = {
        type: null,
        name: '',
        csv_column: '',
        unit_of_measure: '',
        min: '',
        max: '',
        calcForm: '',
        isCombined: false,
        combinedColumns: ''
      };
    },

    handleSavedSensorForm() {
      if (!this.canSave) return;

      const newSensor = {
        name: this.sensorCreated.name,
        csv_column: this.sensorCreated.csv_column,
        unit_of_measure: this.sensorCreated.unit_of_measure,
        min: this.sensorCreated.min,
        max: this.sensorCreated.max,
        calcForm: this.sensorCreated.calcForm,
        isCombined: this.sensorCreated.isCombined || false,
        combinedColumns: this.sensorCreated.isCombined
            ? this.sensorCreated.combinedColumns.split(',').map(c => c.trim())
            : []
      };

      this.$emit("added", newSensor);
      this.sensorModal = false;
      this.resetForm();
    },

    cancel() {
      this.resetForm();
      this.sensorModal = false;
    },

    getSensorTypes() {
      return this.$store.dispatch("sensorTypesRequest", {});
    },

    deleteSensor(sensor) {
      this.sensorToDelete = sensor;
      this.deleteModal = true;
    },

    confirmDelete() {
      if (this.sensorToDelete) {
        this.$emit("deleted", this.sensorToDelete);
      }
      this.cancelDelete();
    },

    cancelDelete() {
      this.sensorToDelete = null;
      this.deleteModal = false;
    }
  },
};
</script>

<style scoped>
/* ============================================
   CSS VARIABLES
   ============================================ */
.sensors-container {
  --primary: #2d5a3d;
  --primary-light: #3d7a52;
  --primary-lighter: #e8f0eb;
  --primary-dark: #1e3d29;
  --accent: #5a9e6f;
  --surface: #ffffff;
  --surface-elevated: #fafbfa;
  --border: #e2e8e4;
  --border-light: #f0f4f1;
  --text-primary: #1a2e1f;
  --text-secondary: #5a6b5e;
  --text-muted: #8a998e;
  --danger: #dc2626;
  --danger-light: #fef2f2;
  --warning: #d97706;
  --shadow-sm: 0 1px 2px rgba(45, 90, 61, 0.05);
  --shadow-md: 0 4px 12px rgba(45, 90, 61, 0.08);
  --shadow-lg: 0 12px 40px rgba(45, 90, 61, 0.15);
  --radius-sm: 6px;
  --radius-md: 10px;
  --radius-lg: 16px;
  --transition: 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
}

.combined-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background: var(--surface-elevated);
  border-radius: var(--radius-sm);
}

.combined-toggle input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.combined-toggle label {
  font-size: 0.875rem;
  color: var(--text-primary);
  cursor: pointer;
  margin: 0;
}

.field-help {
  font-size: 0.75rem;
  color: var(--text-muted);
  margin: 4px 0 0;
  font-style: italic;
}

/* ============================================
   HEADER
   ============================================ */
.sensors-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.header-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-icon {
  font-size: 24px;
  color: var(--accent);
}

.header-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.header-subtitle {
  font-size: 0.8125rem;
  color: var(--text-muted);
  margin: 2px 0 0;
}

/* ============================================
   BUTTONS
   ============================================ */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 16px;
  border-radius: var(--radius-sm);
  font-size: 0.875rem;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: var(--transition);
  white-space: nowrap;
}

.btn .material-symbols-outlined {
  font-size: 18px;
}

.btn-primary {
  background: var(--primary);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: var(--primary-light);
  transform: translateY(-1px);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.btn-ghost {
  background: transparent;
  color: var(--text-secondary);
  border: 1px solid var(--border);
}

.btn-ghost:hover {
  background: var(--surface-elevated);
  border-color: var(--primary);
}

.btn-danger {
  background: var(--danger);
  color: white;
}

.btn-danger:hover {
  background: #b91c1c;
}

.btn-text {
  display: none;
}

@media (min-width: 480px) {
  .btn-text {
    display: inline;
  }
}

.btn-delete {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: var(--text-muted);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: var(--transition);
}

.btn-delete:hover {
  background: var(--danger-light);
  color: var(--danger);
}

.btn-delete .material-symbols-outlined {
  font-size: 18px;
}

/* ============================================
   TABLE VIEW (Desktop)
   ============================================ */
.sensors-table {
  display: none;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  overflow: hidden;
}

@media (min-width: 900px) {
  .sensors-table {
    display: block;
  }
  .sensors-cards {
    display: none;
  }
}

.table-header {
  display: grid;
  grid-template-columns: 1.5fr 1fr 0.7fr 0.6fr 0.6fr 1fr 44px;
  gap: 12px;
  padding: 12px 16px;
  background: var(--surface-elevated);
  border-bottom: 1px solid var(--border);
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.table-header span:last-child {
  text-align: center;
}

.table-row {
  display: grid;
  grid-template-columns: 1.5fr 1fr 0.7fr 0.6fr 0.6fr 1fr 44px;
  gap: 12px;
  padding: 10px 16px;
  align-items: center;
  border-bottom: 1px solid var(--border-light);
  transition: var(--transition);
}

.table-row:last-child {
  border-bottom: none;
}

.table-row:hover {
  background: var(--surface-elevated);
}

.table-row.has-alert {
  background: linear-gradient(90deg, transparent 0%, rgba(217, 119, 6, 0.03) 100%);
}

/* ============================================
   INLINE INPUTS
   ============================================ */
.inline-input {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  font-size: 0.875rem;
  color: var(--text-primary);
  background: transparent;
  transition: var(--transition);
}

.inline-input:hover {
  border-color: var(--border);
  background: var(--surface);
}

.inline-input:focus {
  outline: none;
  border-color: var(--primary);
  background: var(--surface);
  box-shadow: 0 0 0 3px var(--primary-lighter);
}

.inline-input::placeholder {
  color: var(--text-muted);
}

.inline-input.mono {
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 0.8125rem;
}

.inline-input.number-input {
  text-align: center;
}

.inline-input.unit-input {
  text-align: center;
}

/* ============================================
   CELL VALUES
   ============================================ */
.cell-value {
  font-size: 0.875rem;
  color: var(--text-primary);
}

.cell-code {
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 0.8125rem;
  color: var(--primary);
  background: var(--primary-lighter);
  padding: 4px 8px;
  border-radius: 4px;
}

.cell-unit {
  font-size: 0.875rem;
  color: var(--text-secondary);
  text-align: center;
  display: block;
}

.cell-alert {
  font-size: 0.8125rem;
  color: var(--text-muted);
  text-align: center;
  display: block;
}

.cell-alert.active {
  color: var(--warning);
  font-weight: 500;
}

.cell-formula {
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 0.75rem;
  color: var(--text-secondary);
}

/* ============================================
   CARD VIEW (Mobile)
   ============================================ */
.sensors-cards {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sensor-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  overflow: hidden;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: var(--surface-elevated);
  border-bottom: 1px solid var(--border-light);
}

.card-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  color: var(--text-primary);
}

.card-title .material-symbols-outlined {
  font-size: 20px;
  color: var(--accent);
}

.card-title .inline-input {
  font-weight: 600;
}

.card-body {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.card-row {
  display: flex;
  gap: 12px;
}

.card-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.card-field.half {
  flex: 1;
}

.card-field label {
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.card-field code {
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 0.8125rem;
  color: var(--primary);
}

.card-field span {
  font-size: 0.875rem;
  color: var(--text-primary);
}

/* ============================================
   EMPTY STATE
   ============================================ */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 48px 24px;
  background: var(--surface-elevated);
  border: 2px dashed var(--border);
  border-radius: var(--radius-md);
}

.empty-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: var(--primary-lighter);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.empty-icon .material-symbols-outlined {
  font-size: 32px;
  color: var(--accent);
}

.empty-state h4 {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 6px;
}

.empty-state p {
  font-size: 0.875rem;
  color: var(--text-muted);
  margin: 0 0 20px;
  max-width: 280px;
}

/* ============================================
   MODAL
   ============================================ */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 1000;
}

.modal-card {
  background: var(--surface);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  width: 100%;
  max-width: 960px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-card.modal-sm {
  max-width: 380px;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  background: var(--primary);
  color: white;
}

.modal-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.modal-title .material-symbols-outlined {
  font-size: 24px;
}

.modal-title h3 {
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0;
}

.modal-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  color: white;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: var(--transition);
}

.modal-close:hover {
  background: rgba(255, 255, 255, 0.25);
}

.modal-body {
  flex: 1;
  padding: 24px;
  overflow-y: auto;
}

.modal-body.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  background: var(--surface-elevated);
  border-top: 1px solid var(--border);
}

/* ============================================
   TYPE SELECTOR
   ============================================ */
.type-selector {
  margin-bottom: 8px;
}

.type-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-top: 10px;
}

.type-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 14px 10px;
  background: var(--surface-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: var(--transition);
  font-size: 0.75rem;
  color: var(--text-secondary);
}

.type-option:hover {
  border-color: var(--primary);
  background: var(--primary-lighter);
}

.type-option.selected {
  border-color: var(--primary);
  background: var(--primary-lighter);
  color: var(--primary);
}

.type-option .material-symbols-outlined {
  font-size: 22px;
}

.type-select {
  margin-top: 12px;
}

.form-divider {
  height: 1px;
  background: var(--border);
  margin: 20px 0;
}

/* ============================================
   FORM FIELDS
   ============================================ */
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-field.full {
  grid-column: 1 / -1;
}

.field-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--text-secondary);
}

.field-label.required::after {
  content: '*';
  color: var(--danger);
}

.field-hint {
  font-size: 0.6875rem;
  font-weight: 400;
  color: var(--text-muted);
  margin-left: auto;
}

.form-input {
  width: 100%;
  padding: 12px 14px;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  font-size: 0.9375rem;
  color: var(--text-primary);
  background: var(--surface);
  transition: var(--transition);
}

.form-input:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px var(--primary-lighter);
}

.form-input::placeholder {
  color: var(--text-muted);
}

.form-input.mono {
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 0.875rem;
}

/* ============================================
   DELETE MODAL
   ============================================ */
.delete-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 32px 24px 24px;
}

.delete-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--danger-light);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.delete-icon .material-symbols-outlined {
  font-size: 28px;
  color: var(--danger);
}

.delete-content h3 {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 8px;
}

.delete-sensor-name {
  font-size: 1rem;
  font-weight: 500;
  color: var(--primary);
  margin: 0 0 8px;
}

.delete-warning {
  font-size: 0.875rem;
  color: var(--text-muted);
  margin: 0;
}

/* ============================================
   LOADER
   ============================================ */
.loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: var(--text-muted);
  font-size: 0.875rem;
}

.loader-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--border);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ============================================
   MODAL TRANSITIONS
   ============================================ */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.modal-fade-enter-active .modal-card,
.modal-fade-leave-active .modal-card {
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter .modal-card,
.modal-fade-leave-to .modal-card {
  transform: scale(0.95);
  opacity: 0;
}

/* ============================================
   VUETIFY OVERRIDES
   ============================================ */
.type-select >>> .v-input__slot {
  background: var(--surface) !important;
  border: 1px solid var(--border) !important;
  border-radius: var(--radius-sm) !important;
  box-shadow: none !important;
  min-height: 44px !important;
}

/* ============================================
   MATERIAL SYMBOLS
   ============================================ */
.material-symbols-outlined {
  font-family: 'Material Symbols Outlined';
  font-variation-settings:
      'FILL' 0,
      'wght' 400,
      'GRAD' 0,
      'opsz' 24;
}
</style>